import type {
    Account,
    DebtAccount,
    DebtPayDownRule,
    IRAAccount,
    MoneyMapSimulation,
    MonthSnapshot,
    MonthTransfer,
    Rule,
    RuleSet,
    SimulationParams,
    SimulationResult,
} from './types';

function deepCloneAccounts(accounts: Account[]): Account[] {
    // Accounts are flat objects (no nested arrays/objects), so a shallow copy
    // per account is equivalent to a deep clone here. Avoids structuredClone,
    // which isn't available in React Native's Hermes engine.
    return accounts.map((account) => ({ ...account }));
}

function sortByPriority<T extends { priority: number }>(items: T[]): T[] {
    return [...items].sort((a, b) => a.priority - b.priority);
}

function isIRA(account: Account): account is IRAAccount {
    return account.type === 'ROTH_IRA' || account.type === 'TRADITIONAL_IRA';
}

function isDebt(account: Account): account is DebtAccount {
    return account.type === 'DEBT';
}

function applyMonthlyInterest(
    accounts: Account[],
    depositApyPercent: number,
    investmentReturnPercent: number,
): Record<string, number> {
    const interestApplied: Record<string, number> = {};

    for (const account of accounts) {
        let interestCents = 0;

        if (account.type === 'SAVINGS') {
            const monthlyRate = depositApyPercent / 100 / 12;
            interestCents = Math.round(account.balanceCents * monthlyRate);
            account.balanceCents += interestCents;
        } else if (account.type === 'TAXABLE_INVESTMENT' || isIRA(account)) {
            const monthlyRate = investmentReturnPercent / 100 / 12;
            interestCents = Math.round(account.balanceCents * monthlyRate);
            account.balanceCents += interestCents;
        } else if (isDebt(account) && account.balanceCents > 0) {
            const monthlyRate = account.annualInterestRate / 12;
            interestCents = Math.round(account.balanceCents * monthlyRate);
            account.balanceCents += interestCents;
        }

        interestApplied[account.id] = interestCents;
    }

    return interestApplied;
}

/**
 * Apply minimum payments to debt accounts from the bills portion of income.
 * These are already deducted from take-home before the goals pool is
 * calculated, so they reduce debt balances without consuming goals funds.
 *
 * When a debt is fully paid off, the minimum payment that was previously
 * allocated in bills is no longer needed. The "freed" portion flows back
 * into the goals pool that month, accelerating progress on remaining
 * goals and debts. This models the real-world effect of a bill disappearing
 * from the member's monthly obligations.
 */
function applyMinimumPayments(accounts: Account[]): {
    applied: Record<string, number>;
    freedCents: number;
} {
    const applied: Record<string, number> = {};
    let freedCents = 0;

    for (const account of accounts) {
        if (isDebt(account)) {
            if (account.balanceCents > 0) {
                const payment = Math.min(
                    account.minimumPaymentCents,
                    account.balanceCents,
                );
                account.balanceCents -= payment;
                applied[account.id] = payment;
                freedCents += account.minimumPaymentCents - payment;
            } else {
                applied[account.id] = 0;
                freedCents += account.minimumPaymentCents;
            }
        } else {
            applied[account.id] = 0;
        }
    }

    return { applied, freedCents };
}

/**
 * Track cumulative contributions per account per year and per month
 * to enforce monthly/yearly caps and IRA limits.
 */
interface ContributionTracker {
    yearly: Record<string, number>;
    monthly: Record<string, number>;
}

function getContributionCap(
    rule: Rule,
    account: Account,
    tracker: ContributionTracker,
    desiredAmount: number,
): number {
    let cap = desiredAmount;

    if (rule.monthlyContributionMaxCents != null) {
        const trackingId = rule.accountId ?? account.id;
        const alreadyThisMonth = tracker.monthly[trackingId] ?? 0;
        const remainingMonthly =
            rule.monthlyContributionMaxCents - alreadyThisMonth;
        cap = Math.min(cap, Math.max(remainingMonthly, 0));
    }

    if (rule.yearlyContributionMaxCents != null) {
        const trackingId = rule.accountId ?? account.id;
        const alreadyThisYear = tracker.yearly[trackingId] ?? 0;
        const remainingYearly =
            rule.yearlyContributionMaxCents - alreadyThisYear;
        cap = Math.min(cap, Math.max(remainingYearly, 0));
    }

    if (isIRA(account)) {
        const ytd = tracker.yearly[account.id] ?? 0;
        const remainingIRA = account.annualContributionLimitCents - ytd;
        cap = Math.min(cap, Math.max(remainingIRA, 0));
    }

    return cap;
}

function calculateRuleAmount(
    rule: Rule,
    available: number,
    accounts: Account[],
): number {
    switch (rule.allocationType) {
        case 'PERCENTAGE':
            return Math.round(available * rule.percentage);

        case 'MINIMUM_BALANCE': {
            if (rule.accountId == null || rule.accountId === '') return 0;
            const account = accounts.find((a) => a.id === rule.accountId);
            if (!account) return 0;
            const needed =
                rule.targetBalanceCents -
                ('balanceCents' in account ? account.balanceCents : 0);
            return Math.min(Math.max(needed, 0), available);
        }

        case 'DEBT_PAY_DOWN': {
            if (rule.strategy === 'AVALANCHE') {
                return calculateAvalancheAmount(rule, available, accounts);
            }

            if (rule.accountId == null || rule.accountId === '') return 0;
            const debt = accounts.find((a) => a.id === rule.accountId);
            if (!debt || !isDebt(debt) || debt.balanceCents <= 0) return 0;

            const minimum = Math.min(
                debt.minimumPaymentCents,
                debt.balanceCents,
            );
            return Math.min(minimum, available);
        }
    }
}

function calculateAvalancheAmount(
    rule: DebtPayDownRule,
    available: number,
    accounts: Account[],
): number {
    const debts = (rule.accountIds ?? [])
        .map((id) => accounts.find((a) => a.id === id))
        .filter((a): a is DebtAccount => a != null && isDebt(a))
        .filter((d) => d.balanceCents > 0)
        .sort((a, b) => b.annualInterestRate - a.annualInterestRate);

    if (debts.length === 0) return 0;

    let totalNeeded = 0;
    for (const debt of debts) {
        totalNeeded += debt.balanceCents;
    }

    return Math.min(totalNeeded, available);
}

function applyAvalancheTransfer(
    rule: DebtPayDownRule,
    ruleSetName: string,
    amountCents: number,
    accounts: Account[],
    incomeAccountId: string,
    tracker: ContributionTracker,
    transfers: MonthTransfer[],
): void {
    const debts = (rule.accountIds ?? [])
        .map((id) => accounts.find((a) => a.id === id))
        .filter((a): a is DebtAccount => a != null && isDebt(a))
        .filter((d) => d.balanceCents > 0)
        .sort((a, b) => b.annualInterestRate - a.annualInterestRate);

    if (debts.length === 0) return;

    const src = accounts.find((a) => a.id === incomeAccountId);
    let remaining = amountCents;

    // Direct all funds to highest-rate debt first, cascade to next.
    // Background payments (from bills) already cover minimums; this
    // allocation is purely extra principal from the goals pool.
    for (const debt of debts) {
        if (remaining <= 0 || debt.balanceCents <= 0) continue;
        const payment = Math.min(remaining, debt.balanceCents);
        if (payment > 0) {
            debt.balanceCents -= payment;
            if (src && 'balanceCents' in src) {
                src.balanceCents -= payment;
            }
            remaining -= payment;
            transfers.push({
                ruleId: rule.id,
                ruleDescription: rule.description,
                ruleSetName,
                accountId: debt.id,
                amountCents: payment,
            });
            tracker.monthly[debt.id] =
                (tracker.monthly[debt.id] ?? 0) + payment;
            tracker.yearly[debt.id] = (tracker.yearly[debt.id] ?? 0) + payment;
        }
    }
}

function updateAccountBalance(
    accounts: Account[],
    accountId: string,
    amountCents: number,
): void {
    const account = accounts.find((a) => a.id === accountId);
    if (!account) return;

    if (isDebt(account)) {
        account.balanceCents = Math.max(account.balanceCents - amountCents, 0);
    } else {
        account.balanceCents += amountCents;
    }
}

function applyTransfer(
    rule: Rule,
    ruleSetName: string,
    amountCents: number,
    accounts: Account[],
    incomeAccountId: string,
    tracker: ContributionTracker,
    transfers: MonthTransfer[],
): void {
    const accountId = rule.accountId ?? '';
    if (accountId === '') return;

    transfers.push({
        ruleId: rule.id,
        ruleDescription: rule.description,
        ruleSetName,
        accountId,
        amountCents,
    });

    if (accountId !== incomeAccountId) {
        const src = accounts.find((a) => a.id === incomeAccountId);
        if (src && 'balanceCents' in src) {
            src.balanceCents -= amountCents;
        }
        updateAccountBalance(accounts, accountId, amountCents);
    }

    tracker.monthly[accountId] =
        (tracker.monthly[accountId] ?? 0) + amountCents;
    tracker.yearly[accountId] = (tracker.yearly[accountId] ?? 0) + amountCents;
}

/**
 * Allocates funds within a rule set proportionally by splitPercentage.
 * Each rule gets its proportional share of available funds.
 *
 * When a rule is saturated (uses less than allocated),
 * unused funds redistribute proportionally to remaining active rules.
 */
function simulatePercentageSplit(
    ruleSet: RuleSet,
    remainingFunds: number,
    accounts: Account[],
    tracker: ContributionTracker,
    incomeAccountId: string,
): { transfers: MonthTransfer[]; consumed: number } {
    const transfers: MonthTransfer[] = [];

    interface RuleAllocation {
        rule: Rule;
        active: boolean;
    }

    const allocations: RuleAllocation[] = sortByPriority(ruleSet.rules).map(
        (rule) => ({ rule, active: true }),
    );

    let totalConsumed = 0;
    let fundsToAllocate = remainingFunds;
    const MAX_ITERATIONS = 10;

    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
        if (fundsToAllocate <= 0) break;

        const activeRules = allocations.filter((a) => a.active);
        if (activeRules.length === 0) break;

        const totalActivePct = activeRules.reduce(
            (sum, a) => sum + a.rule.splitPercentage,
            0,
        );
        if (totalActivePct <= 0) break;

        let unusedThisRound = 0;
        let anyRuleSaturated = false;

        let allocatedSoFar = 0;
        const ruleAllocations: number[] = [];
        for (let i = 0; i < activeRules.length; i++) {
            const alloc = activeRules[i];
            if (!alloc) continue;
            const normalizedPct = alloc.rule.splitPercentage / totalActivePct;
            if (i === activeRules.length - 1) {
                ruleAllocations.push(fundsToAllocate - allocatedSoFar);
            } else {
                const amt = Math.round(fundsToAllocate * normalizedPct);
                ruleAllocations.push(amt);
                allocatedSoFar += amt;
            }
        }

        for (let ri = 0; ri < activeRules.length; ri++) {
            const alloc = activeRules[ri];
            const allocatedFunds = ruleAllocations[ri] ?? 0;
            if (!alloc) continue;
            if (allocatedFunds <= 0) {
                alloc.active = false;
                anyRuleSaturated = true;
                continue;
            }

            const rule = alloc.rule;
            let ruleUsed = 0;

            if (
                rule.allocationType === 'DEBT_PAY_DOWN' &&
                rule.strategy === 'AVALANCHE'
            ) {
                const desiredAmount = calculateAvalancheAmount(
                    rule,
                    allocatedFunds,
                    accounts,
                );
                const actualAmount = Math.min(desiredAmount, allocatedFunds);
                if (actualAmount > 0) {
                    applyAvalancheTransfer(
                        rule,
                        ruleSet.name,
                        actualAmount,
                        accounts,
                        incomeAccountId,
                        tracker,
                        transfers,
                    );
                    ruleUsed = actualAmount;
                }
            } else {
                const account =
                    rule.accountId != null && rule.accountId !== ''
                        ? accounts.find((a) => a.id === rule.accountId)
                        : undefined;
                if (account) {
                    const desiredAmount = calculateRuleAmount(
                        rule,
                        allocatedFunds,
                        accounts,
                    );
                    const cappedAmount = getContributionCap(
                        rule,
                        account,
                        tracker,
                        desiredAmount,
                    );
                    const actualAmount = Math.min(cappedAmount, allocatedFunds);

                    if (actualAmount > 0) {
                        applyTransfer(
                            rule,
                            ruleSet.name,
                            actualAmount,
                            accounts,
                            incomeAccountId,
                            tracker,
                            transfers,
                        );
                        ruleUsed = actualAmount;
                    }
                }
            }

            totalConsumed += ruleUsed;
            const leftover = allocatedFunds - ruleUsed;
            if (leftover > 0) {
                unusedThisRound += leftover;
                alloc.active = false;
                anyRuleSaturated = true;
            }
        }

        if (!anyRuleSaturated || unusedThisRound <= 0) break;

        fundsToAllocate = unusedThisRound;
    }

    return { transfers, consumed: totalConsumed };
}

function simulateMonth(
    moneyMap: MoneyMapSimulation,
    accounts: Account[],
    tracker: ContributionTracker,
    availableIncomeCents: number,
): { transfers: MonthTransfer[]; leftoverCents: number } {
    const transfers: MonthTransfer[] = [];
    let remainingFunds = availableIncomeCents;

    const incomeAccount = accounts.find(
        (a) => a.id === moneyMap.incomeAccountId,
    );
    if (incomeAccount && 'balanceCents' in incomeAccount) {
        incomeAccount.balanceCents += availableIncomeCents;
    }

    for (const ruleSet of sortByPriority(moneyMap.ruleSets)) {
        if (remainingFunds <= 0) break;

        const { transfers: t, consumed } = simulatePercentageSplit(
            ruleSet,
            remainingFunds,
            accounts,
            tracker,
            moneyMap.incomeAccountId,
        );
        transfers.push(...t);
        remainingFunds -= consumed;
    }

    return { transfers, leftoverCents: remainingFunds };
}

export function runSimulation(
    moneyMap: MoneyMapSimulation,
    params: SimulationParams,
): SimulationResult {
    const accounts = deepCloneAccounts(moneyMap.accounts);
    const months: MonthSnapshot[] = [];

    const tracker: ContributionTracker = { yearly: {}, monthly: {} };

    // Seed the yearly tracker with pre-simulation contributions so the first
    // partial year respects IRA limits. On subsequent year rollovers the tracker
    // resets to {}, giving the full annual limit.
    for (const account of accounts) {
        if (isIRA(account) && account.initialYtdContributionCents > 0) {
            tracker.yearly[account.id] = account.initialYtdContributionCents;
        }
    }

    let currentYear = params.startDate.getFullYear();

    for (let i = 0; i < params.numMonths; i++) {
        const monthDate = new Date(params.startDate);
        monthDate.setMonth(monthDate.getMonth() + i);

        if (monthDate.getFullYear() !== currentYear) {
            currentYear = monthDate.getFullYear();
            tracker.yearly = {};
        }
        tracker.monthly = {};

        const balancesBefore: Record<string, number> = {};
        for (const account of accounts) {
            balancesBefore[account.id] = account.balanceCents;
        }

        const interestApplied = applyMonthlyInterest(
            accounts,
            params.depositApyPercent,
            params.investmentReturnPercent,
        );

        const { applied: minimumPaymentsApplied, freedCents } =
            applyMinimumPayments(accounts);

        const { transfers, leftoverCents } = simulateMonth(
            moneyMap,
            accounts,
            tracker,
            // Goals pool + any minimums freed by paid-off debts this month
            moneyMap.monthlyGoalsCents + freedCents,
        );

        const balancesAfter: Record<string, number> = {};
        for (const account of accounts) {
            balancesAfter[account.id] = account.balanceCents;
        }

        months.push({
            monthIndex: i,
            date: monthDate,
            balancesBefore,
            interestApplied,
            minimumPaymentsApplied,
            freedFromPaidOffDebtsCents: freedCents,
            transfers,
            balancesAfter,
            leftoverCents,
        });
    }

    return { params, moneyMap, months };
}
